/* global axios */
import ApiClient from './ApiClient';

class AnalyticsDashboardAPI extends ApiClient {
  constructor() {
    super('analytics_dashboard', { accountScoped: true, apiVersion: 'v2' });
  }

  /**
   * Get complete overview data for the analytics dashboard
   * @param {Object} params - Query parameters
   * @param {number} params.since - Start timestamp
   * @param {number} params.until - End timestamp
   * @param {number} params.timezone_offset - Timezone offset in hours
   */
  getOverview({ since, until, timezone_offset } = {}) {
    return axios.get(`${this.url}/overview`, {
      params: { since, until, timezone_offset },
    });
  }

  /**
   * Get real-time conversation data for the monitor tab
   */
  getLiveMonitor() {
    return axios.get(`${this.url}/live_monitor`);
  }

  /**
   * Get detailed real-time monitor data with messages
   * Returns human agents, bots, and AI agents with their active conversations and messages
   */
  getLiveMonitorRealtime() {
    return axios.get(`${this.url}/live_monitor_realtime`);
  }

  /**
   * Get campaign statistics
   * @param {Object} params - Query parameters
   * @param {number} params.since - Start timestamp
   * @param {number} params.until - End timestamp
   */
  getCampaigns({ since, until } = {}) {
    return axios.get(`${this.url}/campaigns`, {
      params: { since, until },
    });
  }

  /**
   * Get AI agents statistics
   * @param {Object} params - Query parameters
   * @param {number} params.since - Start timestamp
   * @param {number} params.until - End timestamp
   */
  getAiAgents({ since, until } = {}) {
    return axios.get(`${this.url}/ai_agents`, {
      params: { since, until },
    });
  }

  /**
   * Get CRM/Kanban/Funnel metrics
   * @param {Object} params - Query parameters
   * @param {number} params.since - Start timestamp
   * @param {number} params.until - End timestamp
   */
  getCrm({ since, until } = {}) {
    return axios.get(`${this.url}/crm`, {
      params: { since, until },
    });
  }

  /**
   * Get performance metrics (CSAT, SLA, Bot)
   * @param {Object} params - Query parameters
   * @param {number} params.since - Start timestamp
   * @param {number} params.until - End timestamp
   */
  getPerformance({ since, until } = {}) {
    return axios.get(`${this.url}/performance`, {
      params: { since, until },
    });
  }

  /**
   * Get detailed conversation analytics
   * @param {Object} params - Query parameters
   * @param {number} params.since - Start timestamp
   * @param {number} params.until - End timestamp
   * @param {number} params.timezone_offset - Timezone offset in hours
   */
  getConversationsDetailed({ since, until, timezone_offset } = {}) {
    return axios.get(`${this.url}/conversations_detailed`, {
      params: { since, until, timezone_offset },
    });
  }

  /**
   * Get detailed agent analytics
   * @param {Object} params - Query parameters
   * @param {number} params.since - Start timestamp
   * @param {number} params.until - End timestamp
   * @param {number} params.timezone_offset - Timezone offset in hours
   */
  getAgentsDetailed({ since, until, timezone_offset } = {}) {
    return axios.get(`${this.url}/agents_detailed`, {
      params: { since, until, timezone_offset },
    });
  }

  /**
   * Get detailed inbox/channel analytics
   * @param {Object} params - Query parameters
   * @param {number} params.since - Start timestamp
   * @param {number} params.until - End timestamp
   * @param {number} params.timezone_offset - Timezone offset in hours
   */
  getInboxesDetailed({ since, until, timezone_offset } = {}) {
    return axios.get(`${this.url}/inboxes_detailed`, {
      params: { since, until, timezone_offset },
    });
  }

  /**
   * Get comprehensive system performance metrics
   * @param {Object} params - Query parameters
   * @param {number} params.since - Start timestamp
   * @param {number} params.until - End timestamp
   * @param {number} params.timezone_offset - Timezone offset in hours
   */
  getSystemPerformance({ since, until, timezone_offset } = {}) {
    return axios.get(`${this.url}/system_performance`, {
      params: { since, until, timezone_offset },
    });
  }

  /**
   * Get comprehensive CRM/Funnel analytics
   * @param {Object} params - Query parameters
   * @param {number} params.since - Start timestamp
   * @param {number} params.until - End timestamp
   * @param {number} params.timezone_offset - Timezone offset in hours
   */
  getCrmDetailed({ since, until, timezone_offset } = {}) {
    return axios.get(`${this.url}/crm_detailed`, {
      params: { since, until, timezone_offset },
    });
  }
}

export default new AnalyticsDashboardAPI();
